import { ConnectionStatus } from '@yandex-video-platform/goloom-sdk';
import { eventChannel } from 'redux-saga';
import { all, call, delay, put, select, take, takeLatest, takeLeading } from 'typed-redux-saga';

import { RootState } from '../../../../../init-store';
import { selectIsReconnectFlowV2Enabled } from '../../../../shared/features/config/selectors';
import { goloomSdk } from '../../../../shared/features/goloom-sdk';
import {
    hideNotification,
    NOTIFICATIONS_MAP,
    notify,
} from '../../../../shared/features/notifications/ps-notifications';
import { countMetrika } from '../../../../shared/helpers/metrika';
import offlineMonitor from '../../../../shared/helpers/offline';
import { takeLatestThrottled } from '../../../../shared/helpers/redux-saga';
import { NetworkState } from '../../constants';
import { getDegradedStateStartTimestamp, getDisplayedNetworkState, getNetworkState, getRoomId } from '../../selectors';
import { ConnectionStatusEvent, NotificationId } from '../../types';
import { setOnlineInChat } from '../chat';
import { reconnectConference } from '../conference/slice';
import { unshareScreenAction } from '../screen-sharing/slice';
import { logger, waitingRoomUpdateSaga } from '../waiting-rooms';
import { calculateRemainingDelay, getNetworkNotificationToHide, shouldShowNetworkNotification } from './helpers';
import {
    networkStateOffline,
    networkStateOnline,
    scheduleNetworkStateChange,
    setDisplayedNetworkState,
    setNetworkState,
} from './slice';

enum OFFLINE_EVENTS {
    OFFLINE = 'offline',
    ONLINE = 'online',
    RECONNECTING = 'reconnecting',
    DISCONNECTED = 'disconnected',
}

/**
 * @param {NetworkState} networkState
 */
function* notifyNetworkState(networkState: NetworkState) {
    const roomId = yield* select(getRoomId);

    switch (networkState) {
        case NetworkState.OFFLINE:
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.OFFLINE]()));
            yield* call(countMetrika, 'no_connection_message_shown', roomId);
            break;

        case NetworkState.RECONNECTING:
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.RECONNECTING]()));
            yield* call(countMetrika, 'reconnect_in_progress_message_shown', roomId);
            break;

        case NetworkState.ONLINE:
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.ONLINE]()));
            yield* call(countMetrika, 'reconnected_message_shown', roomId);
            break;
    }
}

export function* scheduleNetworkStateChangeWorker(action: ReturnType<typeof scheduleNetworkStateChange>) {
    const targetState = action.payload;
    const initialNetworkState = yield* select(getDisplayedNetworkState);
    const degradedStateStartTimestamp = yield* select(getDegradedStateStartTimestamp);

    const remainingDelay = calculateRemainingDelay(targetState, degradedStateStartTimestamp);

    yield* delay(remainingDelay);

    const newNetworkState = yield* select(getDisplayedNetworkState);
    const actualNetworkState = yield* select(getNetworkState);

    if (shouldShowNetworkNotification(newNetworkState, targetState) && actualNetworkState === targetState) {
        const notificationToHide = getNetworkNotificationToHide(initialNetworkState);

        if (notificationToHide) {
            yield* put(hideNotification(notificationToHide));
        }

        yield* put(setDisplayedNetworkState(targetState));
        yield* call(notifyNetworkState, targetState);
    }
}

function* watchScheduleNetworkChangeSaga() {
    yield* takeLatest(scheduleNetworkStateChange, scheduleNetworkStateChangeWorker);
}

export function* setOnline() {
    yield* put(networkStateOnline());
    yield* put(setNetworkState(NetworkState.ONLINE));

    yield* put(scheduleNetworkStateChange(NetworkState.ONLINE));

    yield* call(setOnlineInChat);
}

function createOfflineChannel() {
    return eventChannel<OFFLINE_EVENTS>((emitter) => {
        const handleOffline = () => {
            emitter(OFFLINE_EVENTS.OFFLINE);
        };
        const handleOnline = () => {
            emitter(OFFLINE_EVENTS.ONLINE);
        };
        const handleConnectionStatusChanged = ({ current: status }: ConnectionStatusEvent) => {
            if (status === ConnectionStatus.RECONNECTING) {
                emitter(OFFLINE_EVENTS.RECONNECTING);
            } else if (status === ConnectionStatus.CONNECTED) {
                emitter(OFFLINE_EVENTS.ONLINE);
            } else if (status === ConnectionStatus.DISCONNECTED) {
                // Когда мы закрываем крышку, RTC соединение закрывается.
                // @see https://a.yandex-team.ru/arcadia/videoplatform/webrtc/goloom/sdk/browser/lib/src/service/peer-connection/peer-connection.controller.ts?rev=r15635514#L368
                // Мы эмитим DISCONNECTED, попытаться реконектнуться.
                emitter(OFFLINE_EVENTS.DISCONNECTED);
            }
        };

        offlineMonitor.addOfflineEventListener(handleOffline);
        offlineMonitor.addOnlineEventListener(handleOnline);
        goloomSdk.onConnectionStatusChanged.addListener(handleConnectionStatusChanged);

        return () => {
            offlineMonitor.removeOfflineEventListener(handleOffline);
            offlineMonitor.removeOnlineEventListener(handleOnline);
            goloomSdk.onConnectionStatusChanged.removeListener(handleConnectionStatusChanged);
        };
    });
}

/**
 * Обработчик событий offline/online/reconnecting
 */
function* offlineSaga(event: OFFLINE_EVENTS) {
    const isV2Enabled = yield* select(selectIsReconnectFlowV2Enabled);

    switch (event) {
        case OFFLINE_EVENTS.OFFLINE: {
            yield* put(networkStateOffline());
            yield* put(setNetworkState(NetworkState.OFFLINE));

            yield* put(scheduleNetworkStateChange(NetworkState.OFFLINE));

            yield* put(unshareScreenAction());
            break;
        }
        case OFFLINE_EVENTS.ONLINE: {
            yield* call(setOnline);
            break;
        }
        case OFFLINE_EVENTS.RECONNECTING: {
            yield* put(setNetworkState(NetworkState.RECONNECTING));
            yield* put(scheduleNetworkStateChange(NetworkState.RECONNECTING));
            break;
        }
        case OFFLINE_EVENTS.DISCONNECTED: {
            yield call(notifyNetworkState, NetworkState.RECONNECTING);
            yield put(setNetworkState(NetworkState.RECONNECTING));
            if (!isV2Enabled) {
                yield put(reconnectConference({ reason: 'ConferenceStatus was DISCONNECTED' }));
            }
            break;
        }
    }
}

export function* sessionFailedSaga() {
    yield call(notifyNetworkState, NetworkState.RECONNECTING);
    yield put(setNetworkState(NetworkState.RECONNECTING));
    yield put(reconnectConference({ reason: 'Conference session has been failed' }));
}

export function* watchOfflineSaga() {
    const offlineChannel = createOfflineChannel();

    yield takeLeading(offlineChannel, offlineSaga);
}

export function* watchOnlineSaga() {
    yield* takeLatestThrottled(3000, networkStateOffline, function* () {
        const stateSnapshot = yield* select();
        yield* take(networkStateOnline);
        const restoringSagas: Array<(state: RootState) => unknown> = [waitingRoomUpdateSaga];
        try {
            yield* all(restoringSagas.map((saga) => call(saga, stateSnapshot)));
        } catch (error) {
            logger.error(error, {
                method: 'watchOnlineSaga',
            });
        }
    });
}

export { watchScheduleNetworkChangeSaga };
